/* eslint-disable react/no-unescaped-entities */
import { useRef, useState } from "react";
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { IMAGES, INSTAGRAM_URL, HERO_VIDEO, ATMOSPHERE_VIDEO, MOMENT_VIDEO_1, MOMENT_VIDEO_2, TESTIMONIAL_IMAGES, STUDENT_OUTCOMES, STUDIO_ADDRESS, STRIPE_COURSE_LINK } from "../lib/content";
import BookingDialog from "../components/BookingDialog";
import WaitlistDialog from "../components/WaitlistDialog";
import Marquee from "../components/Marquee";
import VideoTile from "../components/VideoTile";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import {
  ChevronDown,
  Instagram,
  Lock,
  Sparkles,
  Heart,
  Flame,
  Users,
  Clock,
  MapPin,
  ArrowUpRight,
} from "lucide-react";

const EASE = [0.19, 1, 0.22, 1];

const Reveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: delay / 1000, ease: EASE }}
  >
    {children}
  </motion.div>
);

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`px-6 sm:px-10 lg:px-16 py-12 sm:py-16 ${className}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const Overline = ({ children, dataTestid, num }) => (
  <p className="overline" data-testid={dataTestid}>
    {num && (
      <span className="font-serif-display italic normal-case tracking-normal text-sm text-[#A01828]/80 mr-3">
        {num}
      </span>
    )}
    <span className="inline-block w-6 h-px bg-[#A01828] align-middle mr-3" />
    {children}
  </p>
);

export default function Landing() {
  const [openTrial, setOpenTrial] = useState(false);
  const [openCourse, setOpenCourse] = useState(false);
  const [openWaitlist, setOpenWaitlist] = useState(false);
  const [defaultGroup, setDefaultGroup] = useState(undefined);

  const openBooking = (g) => {
    setDefaultGroup(g || undefined);
    setOpenTrial(true);
  };

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroMediaY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const heroMediaScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);
  const heroFade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const glowX = useMotionValue(-600);
  const glowY = useMotionValue(-600);
  const glowXSpring = useSpring(glowX, { stiffness: 50, damping: 20 });
  const glowYSpring = useSpring(glowY, { stiffness: 50, damping: 20 });
  const heroGlow = useMotionTemplate`radial-gradient(520px circle at ${glowXSpring}px ${glowYSpring}px, rgba(160,24,40,0.16), transparent 70%)`;

  const onHeroPointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    glowX.set(e.clientX - rect.left);
    glowY.set(e.clientY - rect.top);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#FDFBF7] selection:bg-[#7A1220]">
      {/* Top minimal nav */}
      <nav
        className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-black/40 border-b border-white/5"
        data-testid="top-nav"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center leading-none" data-testid="nav-logo">
            <img
              src={IMAGES.logo}
              alt="Grand Dance Studio"
              className="h-10 sm:h-12 w-auto opacity-95 drop-shadow-[0_2px_14px_rgba(160,24,40,0.45)] hover:opacity-100 hover:drop-shadow-[0_2px_18px_rgba(160,24,40,0.7)] transition-all duration-300"
            />
          </a>
          <div className="flex items-center gap-4">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-[#A01828] transition-colors"
              data-testid="nav-instagram"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <button
              onClick={() => openBooking()}
              data-testid="nav-cta-button"
              className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-[#A01828] hover:text-[#A01828] transition-colors"
            >
              Забронювати
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section
        id="top"
        ref={heroRef}
        onPointerMove={onHeroPointerMove}
        data-testid="hero-section"
        className="relative min-h-[100svh] flex items-end overflow-hidden"
      >
        <motion.div
          className="absolute inset-0 will-change-transform"
          style={{ y: heroMediaY, scale: heroMediaScale }}
        >
          <VideoTile
            src={HERO_VIDEO}
            poster={IMAGES.hero}
            className="w-full h-full object-cover object-center"
            data-testid="hero-video"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-[#0A0A0C]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{ background: heroGlow }}
        />

        <motion.div
          style={{ opacity: heroFade }}
          className="relative z-10 px-6 sm:px-10 lg:px-16 pb-28 sm:pb-40 pt-32 w-full"
        >
          <div className="max-w-6xl mx-auto">
            <motion.p
              className="overline mb-6"
              data-testid="hero-overline"
              initial={{ opacity: 0, x: -18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            >
              <span className="inline-block w-6 h-px bg-[#A01828] align-middle mr-3" />
              Wrocław · Latin Solo · Lady Latina
            </motion.p>
            <h1
              data-testid="hero-headline"
              className="font-serif-display text-5xl sm:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tight max-w-3xl"
            >
              <MaskedLine delay={0.35}>Латина</MaskedLine>
              <MaskedLine delay={0.47}>для жінок</MaskedLine>
              <MaskedLine delay={0.59}>
                <span className="italic text-[#A01828]">у Вроцлаві</span>
              </MaskedLine>
            </h1>
            <Reveal delay={750}>
              <p
                data-testid="hero-subheadline"
                className="mt-8 max-w-xl text-lg sm:text-xl text-white/80 font-light leading-relaxed"
              >
                Танці, які повертають тіло, жіночність, впевненість і кайф від себе.
              </p>
            </Reveal>
            <Reveal delay={900}>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
                <motion.button
                  onClick={() => openBooking()}
                  data-testid="hero-cta-button"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="group inline-flex items-center justify-center gap-3 bg-[#7A1220] hover:bg-[#A01828] transition-colors duration-300 text-white px-8 h-14 text-xs uppercase tracking-[0.2em] font-medium shadow-[0_0_40px_rgba(160,24,40,0.35)]"
                >
                  Забронювати пробне заняття
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </motion.button>
                <div className="text-xs uppercase tracking-widest text-white/50">
                  50 zł · пробне заняття
                </div>
              </div>
            </Reveal>
            <Reveal delay={1050}>
              <p
                data-testid="hero-trust-text"
                className="mt-10 text-xs sm:text-sm text-white/50 tracking-wide max-w-2xl"
              >
                Групи для початківців · Без партнера · Без досвіду · У жіночій атмосфері
              </p>
            </Reveal>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:block"
        >
          <ChevronDown className="w-5 h-5 text-white/40 animate-bounce" />
        </motion.div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <Marquee />

      {/* TRAINER (moved up - introduction first) */}
      <Section id="trainer" className="border-t border-white/5">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.trainer}
                alt="Анастасія Голенкова — засновниця"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                data-testid="trainer-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/90 mb-3">
                  <span className="w-5 h-px bg-[#A01828]" />
                  засновниця · тренерка
                </p>
                <p className="font-serif-display text-4xl text-white leading-tight">
                  Анастасія
                  <br />
                  <span className="italic">Голенкова</span>
                </p>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal>
              <Overline dataTestid="trainer-overline" num="01">Про студію</Overline>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-light mt-6 leading-tight">
                Простір, де жінка
                <br />
                <span className="italic text-[#A01828]">повертається до себе</span>.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-8 space-y-5 text-white/70 text-base sm:text-lg leading-relaxed">
                <p>
                  Більше <span className="text-[#FDFBF7]">20 років тренерського досвіду</span> у спортивних бальних
                  танцях. Турніри, збори, перемоги і поразки, життя в залах — це було моїм повсякденням.
                </p>
                <p>
                  В еміграції у 2023 році я заснувала <span className="text-[#FDFBF7]">Grand Dance Studio</span> у
                  Вроцлаві — для таких самих жінок, як і я: змучених, розгублених, тих, кому був необхідний ковток
                  свіжого повітря. І саме цим повітрям стали наші заняття.
                </p>
                <p>
                  Зараз ми всі адаптувалися до іншого життя — але студія залишилась нашим{" "}
                  <span className="italic text-[#FDFBF7]">місцем сили</span>. Тут не просто танці. Тут підтримка,
                  розуміння, розвиток, ментальне розвантаження і неймовірна енергетика.
                </p>
                <p className="font-serif-display italic text-2xl text-[#FDFBF7] border-l-2 border-[#7A1220] pl-5">
                  «Танець — це не вміння. Це дозвіл собі бути живою.»
                </p>
                <p className="text-xs uppercase tracking-[0.3em] text-[#A01828] pt-2">
                  #нетанцямиєдиними
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* SOLD OUT — Anastasia's beginner groups are FULL · next enrollment in March */}
      <section
        id="september"
        data-testid="september-launch"
        className="relative border-t border-white/5 px-6 sm:px-10 lg:px-16 py-14 sm:py-20 overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 right-0 w-[420px] h-[420px] rounded-full bg-[#A01828]/20 blur-[130px]" />
          <div className="absolute -bottom-32 -left-10 w-[380px] h-[380px] rounded-full bg-[#7A1220]/25 blur-[130px]" />
        </div>
        <div className="relative max-w-6xl mx-auto">
          <Reveal>
            <span className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.03] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-white/60">
              <Lock className="w-3 h-3 text-[#A01828]" />
              Групи Анастасії · заповнені
            </span>
          </Reveal>
          <Reveal delay={100}>
            <h2
              data-testid="september-headline"
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light mt-5 leading-[1.05] max-w-3xl"
            >
              Місця до Анастасії
              <br />
              <span className="italic text-[#A01828]">розлетілися</span>.
              <br />
              Наступний набір — березень.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-5 text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl">
              Три початкові групи закрилися повністю. Залиш ім'я та телефон — і ти{" "}
              <span className="text-[#FDFBF7]">першою дізнаєшся</span> про відкриття
              березневого набору.
            </p>
          </Reveal>
          <Reveal delay={250}>
            <p className="mt-3 inline-flex items-start gap-2 text-white/60 text-sm">
              <MapPin className="w-4 h-4 mt-0.5 text-[#A01828] shrink-0" />
              <span>Krakowska 56-62, Wrocław</span>
            </p>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-3 gap-px bg-white/5">
            {[
              { day: "Вівторок", time: "20:00", value: "tue-2000-nastya" },
              { day: "Четвер", time: "18:00", value: "thu-1800-nastya" },
              { day: "Субота", time: "12:00", value: "sat-1200-nastya" },
            ].map((s, i) => (
              <Reveal key={s.value} delay={i * 100} className="h-full">
                <div
                  data-testid={`book-slot-${s.value}`}
                  aria-disabled="true"
                  className="w-full h-full bg-[#0A0A0C] p-8 sm:p-10 text-left flex flex-col opacity-45 select-none"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Clock className="w-4 h-4 text-white/40" strokeWidth={1.5} />
                    <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-white/50 border border-white/15 px-2 py-1">
                      <Lock className="w-2.5 h-2.5" /> місць немає
                    </span>
                  </div>
                  <p className="font-serif-display text-3xl sm:text-4xl text-white/60">{s.day}</p>
                  <p className="font-serif-display text-5xl sm:text-6xl italic text-white/50 mt-2 line-through decoration-[#A01828]/60 decoration-1">
                    {s.time}
                  </p>
                  <div className="mt-auto pt-6">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                      група заповнена
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-px bg-white/5 border border-white/10">
            <Reveal className="h-full">
              <div
                className="bg-[#0A0A0C] p-8 sm:p-10 h-full flex flex-col"
                data-testid="waitlist-card"
              >
                <p className="text-xs uppercase tracking-[0.25em] text-[#A01828] font-medium">
                  Лист очікування
                </p>
                <h3 className="font-serif-display text-2xl sm:text-3xl font-light mt-3 leading-tight">
                  Хочеш місце у березневій групі?
                </h3>
                <p className="text-white/60 text-sm sm:text-base leading-relaxed mt-3">
                  Залиш ім'я та номер телефону — ми напишемо тобі особисто, щойно відкриється
                  набір. У березні місця розберуть так само швидко.
                </p>
                <motion.button
                  onClick={() => setOpenWaitlist(true)}
                  data-testid="waitlist-open-button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mt-8 inline-flex items-center justify-center gap-3 bg-[#7A1220] hover:bg-[#A01828] transition-colors duration-300 text-white px-7 h-12 text-xs uppercase tracking-[0.2em] font-medium w-full sm:w-auto"
                >
                  Повідомте мені про набір
                  <ArrowUpRight className="w-4 h-4" />
                </motion.button>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="september-instagram-link"
                  className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#A01828] transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                  Танцюєш з досвідом? — пиши в Instagram-direct
                </a>
              </div>
            </Reveal>
            <Reveal delay={120} className="h-full">
              <div
                className="bg-gradient-to-br from-[#7A1220]/20 via-[#0A0A0C] to-[#0A0A0C] p-8 sm:p-10 h-full flex flex-col border-l-2 border-[#A01828]"
                data-testid="september-course-upsell"
              >
                <p className="text-xs uppercase tracking-[0.25em] text-[#A01828] font-medium">
                  Не хочеш чекати до березня?
                </p>
                <h3 className="font-serif-display text-2xl sm:text-3xl font-light mt-3 leading-tight">
                  Онлайн-урок <span className="italic text-[#A01828]">«Гіпно-бедра»</span>
                </h3>
                <p className="text-white/60 text-sm sm:text-base leading-relaxed mt-3">
                  Перше заняття з Анастасією — вже сьогодні, вдома, у своєму темпі. Пластика
                  стегон, жіночність і м'який старт перед залою.
                </p>
                <motion.a
                  href={STRIPE_COURSE_LINK}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="september-course-cta"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mt-8 inline-flex items-center justify-center gap-3 border border-[#A01828]/60 hover:bg-[#7A1220]/30 transition-colors duration-300 text-[#FDFBF7] px-7 h-12 text-xs uppercase tracking-[0.2em] font-medium w-full sm:w-auto"
                >
                  Купити онлайн-урок · 49 zł
                  <ArrowUpRight className="w-4 h-4" />
                </motion.a>
                <p className="mt-4 text-[11px] text-white/40">
                  Безпечна оплата через Stripe. Доступ до уроку — одразу після оплати.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LEVELS + SCHEDULE + TRIAL — combined booking hub */}
      <Section id="schedule" className="border-t border-white/5">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline dataTestid="levels-overline" num="02">Рівні · Розклад · Бронювання</Overline>
            </Reveal>
            <Reveal delay={100}>
              <h2
                data-testid="schedule-headline"
                className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light mt-6 leading-tight"
              >
                Обери свій час —<br />
                і <span className="italic text-[#A01828]">забронюй пробне</span>.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 text-white/60 text-base sm:text-lg max-w-xl leading-relaxed">
                Пробне заняття — <span className="text-[#FDFBF7]">50 zł</span>. Відкриті місця — у групі{" "}
                <span className="text-[#FDFBF7]">Аліни</span> (п'ятниця 19:00). Групи Анастасії
                заповнені — наступний набір у <span className="text-[#FDFBF7]">березні</span>.
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-5" delay={250}>
            <div className="border border-white/10 p-6 bg-[#0E0E11]">
              <p className="text-xs uppercase tracking-[0.25em] text-white/50 mb-4">Як це працює</p>
              <ol className="space-y-2 text-white/75 text-sm">
                <li className="flex gap-3"><span className="font-serif-display italic text-[#A01828] w-5">01</span> Обираєш день і час</li>
                <li className="flex gap-3"><span className="font-serif-display italic text-[#A01828] w-5">02</span> Заповнюєш контакти</li>
                <li className="flex gap-3"><span className="font-serif-display italic text-[#A01828] w-5">03</span> Оплачуєш 50 zł — місце фіксується</li>
              </ol>
            </div>
          </Reveal>
        </div>

        {/* STEP I — primary, accent (Friday slot with Alina OPEN · Anastasia's groups FULL) */}
        <Reveal>
          <div
            className="border border-[#A01828]/50 bg-gradient-to-br from-[#7A1220]/15 via-[#0A0A0C] to-[#0A0A0C] p-6 sm:p-10"
            data-testid="schedule-step1"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#A01828] font-medium">
                  STEP I · для початківців
                </p>
                <h3 className="font-serif-display text-3xl sm:text-4xl font-light mt-2">
                  Початковий рівень
                </h3>
                <p className="text-white/55 text-sm mt-1">
                  Раз на тиждень · відкрита група <span className="text-[#FDFBF7]">Аліни</span> · групи{" "}
                  <span className="text-[#FDFBF7]">Анастасії</span> заповнені
                </p>
              </div>
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#A01828]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A01828] animate-pulse-dot" />
                п'ятниця · набір відкритий
              </span>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-px bg-white/5">
              {[
                { day: "П'ятниця", time: "19:00", value: "fri-1900-alina", trainer: "Аліна", addr: "Piłsudskiego 74" },
                { day: "Вівторок", time: "20:00", value: "tue-2000-nastya", trainer: "Анастасія", addr: "Krakowska 56-62", sold: true },
                { day: "Четвер", time: "18:00", value: "thu-1800-nastya", trainer: "Анастасія", addr: "Krakowska 56-62", sold: true },
                { day: "Субота", time: "12:00", value: "sat-1200-nastya", trainer: "Анастасія", addr: "Krakowska 56-62", sold: true },
              ].map((s) =>
                s.sold ? (
                  <div
                    key={s.value}
                    data-testid={`book-slot-${s.value}`}
                    aria-disabled="true"
                    className="bg-[#0A0A0C] text-left p-6 sm:p-8 opacity-45 select-none"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <Clock className="w-4 h-4 text-white/40" strokeWidth={1.5} />
                          <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-white/50 border border-white/15 px-2 py-1">
                            <Lock className="w-2.5 h-2.5" /> місць немає
                          </span>
                        </div>
                        <p className="font-serif-display text-2xl sm:text-3xl text-white/60">{s.day}</p>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">
                          тренерка · {s.trainer}
                        </p>
                        <p className="mt-2 flex items-start gap-1.5 text-white/40 text-xs">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                          <span>{s.addr}</span>
                        </p>
                      </div>
                      <p className="font-serif-display text-4xl sm:text-5xl text-white/50 italic line-through decoration-[#A01828]/60 decoration-1">
                        {s.time}
                      </p>
                    </div>
                  </div>
                ) : (
                  <button
                    key={s.value}
                    onClick={() => openBooking(s.value)}
                    data-testid={`book-slot-${s.value}`}
                    className="bg-[#0A0A0C] hover:bg-[#7A1220]/30 transition-colors duration-300 text-left group p-6 sm:p-8"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Clock className="w-4 h-4 text-[#A01828] mb-3" strokeWidth={1.5} />
                        <p className="font-serif-display text-2xl sm:text-3xl text-white/90">{s.day}</p>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-2">
                          тренерка · {s.trainer}
                        </p>
                        <p className="mt-2 flex items-start gap-1.5 text-white/50 text-xs">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#A01828]" />
                          <span>{s.addr}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-serif-display text-4xl sm:text-5xl text-[#FDFBF7] italic">
                          {s.time}
                        </p>
                        <ArrowUpRight className="w-4 h-4 text-[#A01828] ml-auto mt-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </div>
                    </div>
                  </button>
                )
              )}
            </div>

            <div className="mt-6 flex items-start gap-3 text-white/60 text-xs sm:text-sm leading-relaxed">
              <span className="w-4 h-px bg-[#A01828] mt-2 shrink-0" />
              <p>
                Пробне заняття — <span className="text-[#FDFBF7]">50 zł</span>. Після оплати надішли
                підтвердження в Instagram-direct і вкажи групу. Групи Анастасії заповнені —{" "}
                <a
                  href="#september"
                  data-testid="schedule-waitlist-link"
                  className="text-[#A01828] hover:text-[#FDFBF7] underline underline-offset-4 transition-colors"
                >
                  лист очікування на березень
                </a>{" "}
                вже відкритий.
              </p>
            </div>
          </div>
        </Reveal>

        {/* STEP II + STARS */}
        <div className="mt-8 grid lg:grid-cols-2 gap-px bg-white/5">
          {[
            {
              code: "STEP II",
              title: "Продовжуючий · від 1 року",
              sub: "Закрита група — набір пізніше",
              testid: "schedule-step-ii",
              closed: true,
              badge: "закрита",
              slots: [
                { day: "Вівторок", time: "18:00", value: "tue-1800-step2" },
                { day: "Середа", time: "11:00", value: "wed-1100-step2" },
                { day: "Четвер", time: "19:00", value: "thu-1900-step2" },
                { day: "Субота", time: "13:00", value: "sat-1300-step2" },
              ],
            },
            {
              code: "STARS",
              title: "Просунутий · від 2 років",
              sub: "Закрита група з досвідом — пробне не продається",
              testid: "schedule-stars",
              closed: true,
              badge: "закрита",
              slots: [
                { day: "Вівторок", time: "19:00", value: "tue-1900-stars" },
                { day: "Четвер", time: "20:00", value: "thu-2000-stars" },
                { day: "Субота", time: "14:00", value: "sat-1400-stars" },
              ],
            },
          ].map((lvl, idx) => (
            <Reveal key={lvl.code} delay={idx * 120}>
              <div className="bg-[#0A0A0C] p-8 sm:p-10 h-full" data-testid={lvl.testid}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs uppercase tracking-[0.3em] text-white/40 font-medium">
                    {lvl.code}
                  </p>
                  {lvl.closed && (
                    <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-white/50 border border-white/15 px-2 py-1">
                      <Lock className="w-2.5 h-2.5" /> {lvl.badge}
                    </span>
                  )}
                </div>
                <h3 className="font-serif-display text-2xl sm:text-3xl font-light mt-3">
                  {lvl.title}
                </h3>
                <p className="text-white/50 mt-1 text-sm">{lvl.sub}</p>
                <ul className="mt-6 divide-y divide-white/5 border-t border-white/5">
                  {lvl.slots.map((s) => (
                    <li key={s.value}>
                      {lvl.closed ? (
                        <div
                          data-testid={`book-slot-${s.value}`}
                          className="w-full flex items-center justify-between py-3 text-sm sm:text-base text-left opacity-60"
                        >
                          <span className="text-white/50">{s.day}</span>
                          <span className="font-serif-display italic text-2xl text-white/50">
                            {s.time}
                          </span>
                        </div>
                      ) : (
                        <button
                          onClick={() => openBooking(s.value)}
                          data-testid={`book-slot-${s.value}`}
                          className="w-full flex items-center justify-between py-3 text-sm sm:text-base text-left group hover:text-white"
                        >
                          <span className="text-white/70 group-hover:text-white">{s.day}</span>
                          <span className="flex items-center gap-3">
                            <span className="font-serif-display italic text-2xl text-[#FDFBF7]">
                              {s.time}
                            </span>
                            <ArrowUpRight className="w-3.5 h-3.5 text-white/30 group-hover:text-[#A01828] transition-colors" />
                          </span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {lvl.closed && (
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="stars-instagram-link"
                    className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#A01828] transition-colors"
                  >
                    <Instagram className="w-4 h-4" /> Запит — в Instagram-direct
                  </a>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200}>
          <p className="mt-8 text-sm text-white/50 leading-relaxed max-w-2xl">
            Розклад може коригуватися. Після оплати графік не регулюється — бронювання не переноситься і не заморожується.
          </p>
        </Reveal>

        {/* Subscription tiers */}
        <Reveal delay={300}>
          <div className="mt-14" data-testid="subscription-card">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
              <div>
                <p className="overline">Після пробного</p>
                <h3 className="font-serif-display text-3xl sm:text-4xl font-light mt-3 leading-tight">
                  Місячний абонемент
                </h3>
              </div>
              <p className="text-white/50 text-xs sm:text-sm max-w-xs sm:text-right">
                Обери свій тариф залежно від рівня та частоти занять.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-px bg-white/5 border border-white/10">
              {[
                {
                  tag: "STEP I / STEP II",
                  title: "Початковий · Продовжуючий",
                  price: "200",
                  meta: "4 заняття / місяць",
                  freq: "раз на тиждень",
                  primary: true,
                  testid: "sub-tier-step",
                },
                {
                  tag: "STARS",
                  title: "Просунутий",
                  sub: "Субота · 14:00–15:30",
                  price: "280",
                  meta: "4 заняття / місяць",
                  freq: "раз на тиждень",
                  primary: false,
                  testid: "sub-tier-stars-once",
                },
                {
                  tag: "STARS · 2× на тиждень",
                  title: "Просунутий · максимум",
                  sub: "Вівторок 19:00 + Четвер 20:00",
                  price: "350",
                  meta: "8 занять / місяць",
                  freq: "двічі на тиждень",
                  primary: false,
                  testid: "sub-tier-stars-twice",
                },
              ].map((t) => (
                <div
                  key={t.tag}
                  data-testid={t.testid}
                  className={`p-6 sm:p-8 flex flex-col ${
                    t.primary
                      ? "bg-gradient-to-br from-[#7A1220]/20 via-[#0A0A0C] to-[#0A0A0C] border-l-2 border-[#A01828]"
                      : "bg-[#0A0A0C]"
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#A01828] font-medium">
                    {t.tag}
                  </p>
                  <h4 className="font-serif-display text-xl sm:text-2xl font-light mt-3 leading-tight">
                    {t.title}
                  </h4>
                  {t.sub && (
                    <p className="text-white/50 text-xs mt-1">{t.sub}</p>
                  )}
                  <div className="mt-6 flex items-baseline gap-2">
                    <p className="font-serif-display text-5xl text-[#FDFBF7]">{t.price}</p>
                    <p className="text-white/70 text-xs uppercase tracking-widest">zł / міс</p>
                  </div>
                  <p className="text-white/60 text-sm mt-2">{t.meta}</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.25em] mt-1">
                    {t.freq}
                  </p>
                </div>
              ))}
            </div>

            {/* Multi-access tiers */}
            <div
              className="mt-6 grid md:grid-cols-2 gap-px bg-white/5 border border-white/10"
              data-testid="multi-access-tiers"
            >
              {[
                {
                  price: "400",
                  title: "Доступ до 3-х груп",
                  meta: "Замість двох — три групи на твій вибір",
                  freq: "розширений доступ",
                  testid: "sub-tier-multi-3",
                },
                {
                  price: "500",
                  title: "Доступ до всіх діючих груп",
                  meta: "Ходи на будь-яку з відкритих груп студії",
                  freq: "максимальний доступ",
                  testid: "sub-tier-multi-all",
                },
              ].map((t) => (
                <div
                  key={t.price}
                  data-testid={t.testid}
                  className="p-6 sm:p-8 bg-gradient-to-br from-[#0E0E11] via-[#0A0A0C] to-[#0A0A0C] flex flex-col"
                >
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-medium">
                    Розширений абонемент
                  </p>
                  <h4 className="font-serif-display text-xl sm:text-2xl font-light mt-3 leading-tight">
                    {t.title}
                  </h4>
                  <div className="mt-6 flex items-baseline gap-2">
                    <p className="font-serif-display text-5xl text-[#FDFBF7]">{t.price}</p>
                    <p className="text-white/70 text-xs uppercase tracking-widest">zł / міс</p>
                  </div>
                  <p className="text-white/60 text-sm mt-2">{t.meta}</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.25em] mt-1">
                    {t.freq}
                  </p>
                </div>
              ))}
            </div>

            <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-white/70 text-sm">
              {[
                "Заняття не заморожуються і не переносяться.",
                "Один, завчасно відомий, пропуск можна відпрацювати в іншій групі подібного рівня.",
                "Доступ до закритої Telegram-групи з відеоуроками хореографій.",
                "Анонси подій студії — #нетанцямиєдиними.",
              ].map((item, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="font-serif-display text-lg text-[#A01828] italic shrink-0 w-6">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </Section>
      {/* ATMOSPHERIC VIDEO STRIP */}
      <section
        className="relative h-[40svh] sm:h-[50svh] overflow-hidden border-t border-white/5"
        data-testid="atmosphere-strip"
      >
        <VideoTile
          src={ATMOSPHERE_VIDEO}
          poster={IMAGES.trainerAlt}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C] via-black/40 to-[#0A0A0C]" />
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <Reveal>
            <div className="max-w-3xl text-center">
              <p className="overline mb-6" style={{ color: '#A01828' }}>
                Атмосфера студії
              </p>
              <p className="font-serif-display text-3xl sm:text-5xl lg:text-6xl font-light italic text-white/95 leading-[1.1]">
                «Тут не просто танці.
                <br />
                Тут жінка <span className="text-[#A01828]">вдихає себе</span>.»
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      {/* SECONDARY PRODUCT: Hipno-bedra */}
      <Section id="course" className="border-t border-white/5">
        <Reveal>
          <Overline dataTestid="course-overline" num="03">Онлайн-урок</Overline>
        </Reveal>
        <div className="mt-6 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6" delay={100}>
            <div className="relative aspect-[5/6] sm:aspect-[4/5] overflow-hidden group">
              <img
                src={IMAGES.course}
                alt="Hipno-bedra online lesson"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                data-testid="course-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute top-6 left-6">
                <span className="inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-white/80">
                  online · 49 zł
                </span>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal delay={150}>
              <h2
                data-testid="course-headline"
                className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight"
              >
                Хочеш почати
                <br />
                <span className="italic text-[#A01828]">вдома?</span>
              </h2>
            </Reveal>
            <Reveal delay={250}>
              <p className="mt-6 text-white/70 text-base sm:text-lg leading-relaxed max-w-xl">
                Онлайн-урок «Гіпно-бедра» — м'який старт для того, щоб відчути свої стегна, пластику та жіночність
                ще до заняття в залі.
              </p>
            </Reveal>
            <Reveal delay={350}>
              <ul className="mt-8 space-y-3 text-white/60 text-sm sm:text-base">
                {[
                  "Розігрів та підготовка тіла",
                  "Техніка руху стегон",
                  "Варіації «вісімок»",
                  "Коротка жіночна комбінація",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="w-4 h-px bg-[#A01828]" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={450}>
              <button
                onClick={() => setOpenCourse(true)}
                data-testid="course-cta-button"
                className="mt-10 inline-flex items-center gap-3 bg-[#7A1220] hover:bg-[#A01828] active:scale-[0.99] text-white px-8 h-14 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 shadow-[0_0_30px_rgba(160,24,40,0.35)] hover:shadow-[0_0_40px_rgba(160,24,40,0.55)]"
              >
                Купити онлайн-урок · 49 zł
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </Reveal>
          </div>
        </div>
      </Section>
      {/* TESTIMONIALS */}
      <section
        id="testimonials"
        className="relative border-t border-white/5 px-6 sm:px-10 lg:px-16 py-12 sm:py-16 overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full bg-[#A01828]/15 blur-[120px]" />
          <div className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-[#7A1220]/20 blur-[120px]" />
        </div>
        <div className="relative max-w-6xl mx-auto">
          <Reveal>
            <Overline dataTestid="testimonials-overline" num="04">Жінки про студію</Overline>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light mt-4 max-w-2xl leading-tight">
              Те, що залишається <span className="italic text-[#A01828]">після першого разу</span>.
            </h2>
          </Reveal>

          <Reveal delay={150}>
            <Carousel
              className="mt-8"
              opts={{ loop: true, align: "start", dragFree: true }}
              data-testid="testimonials-carousel"
            >
              <CarouselContent className="-ml-3">
                {TESTIMONIAL_IMAGES.map((t, i) => (
                  <CarouselItem
                    key={i}
                    className="pl-3 basis-[55%] sm:basis-[35%] md:basis-[28%] lg:basis-[22%]"
                    data-testid={`testimonial-${i}`}
                  >
                    <figure className="bg-[#0E0E11] border border-white/10 hover:border-[#A01828]/40 transition-colors duration-300 overflow-hidden">
                      <div className="bg-black">
                        <img
                          src={t.src}
                          alt={`Відгук — ${t.name}`}
                          loading="lazy"
                          className="w-full h-auto block"
                        />
                      </div>
                      <figcaption className="px-3 py-2 flex items-center gap-2 border-t border-white/5">
                        <span className="w-3 h-px bg-[#A01828] shrink-0" />
                        <p className="text-[10px] uppercase tracking-widest text-white/50 truncate">
                          {t.name}
                        </p>
                      </figcaption>
                    </figure>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex items-center justify-between gap-3 mt-6">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                  Свайп → · {TESTIMONIAL_IMAGES.length} відгуків
                </p>
                <div className="flex items-center gap-2">
                  <CarouselPrevious
                    className="static translate-y-0 translate-x-0 bg-transparent border border-white/15 text-white hover:bg-[#7A1220] hover:border-[#7A1220] rounded-none w-10 h-10"
                    data-testid="testimonials-prev"
                  />
                  <CarouselNext
                    className="static translate-y-0 translate-x-0 bg-transparent border border-white/15 text-white hover:bg-[#7A1220] hover:border-[#7A1220] rounded-none w-10 h-10"
                    data-testid="testimonials-next"
                  />
                </div>
              </div>
            </Carousel>
          </Reveal>
        </div>
      </section>

      {/* WHAT STUDENTS GET — real survey screenshots */}
      <section
        className="relative border-t border-white/5 px-6 sm:px-10 lg:px-16 py-12 sm:py-16 overflow-hidden"
        data-testid="outcomes-section"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 right-0 w-[420px] h-[420px] rounded-full bg-[#7A1220]/15 blur-[120px]" />
        </div>
        <div className="relative max-w-6xl mx-auto">
          <Reveal>
            <Overline dataTestid="outcomes-overline" num="05">Що отримують учениці</Overline>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light mt-4 max-w-3xl leading-tight">
              Прямі відповіді від учениць —<br />
              <span className="italic text-[#A01828]">що вони отримують у Grand Dance Studio</span>.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-4 text-white/55 text-sm sm:text-base max-w-2xl">
              Ці скріни — реальні відповіді з нашого опитування у Instagram. Не маркетинг — просто слова жінок,
              які залишилися.
            </p>
          </Reveal>

          <Reveal delay={150}>
            <Carousel
              className="mt-8"
              opts={{ loop: true, align: "start", dragFree: true }}
              data-testid="outcomes-carousel"
            >
              <CarouselContent className="-ml-3">
                {STUDENT_OUTCOMES.map((src, i) => (
                  <CarouselItem
                    key={src}
                    className="pl-3 basis-[55%] sm:basis-[35%] md:basis-[28%] lg:basis-[22%]"
                    data-testid={`outcome-${i}`}
                  >
                    <figure className="bg-white border border-white/10 hover:border-[#A01828]/40 transition-colors duration-300 overflow-hidden">
                      <img
                        src={src}
                        alt="Що отримують учениці Grand Dance Studio"
                        loading="lazy"
                        className="w-full h-auto block"
                      />
                    </figure>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex items-center justify-between gap-3 mt-6">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                  Свайп → · {STUDENT_OUTCOMES.length} відповідей
                </p>
                <div className="flex items-center gap-2">
                  <CarouselPrevious
                    className="static translate-y-0 translate-x-0 bg-transparent border border-white/15 text-white hover:bg-[#7A1220] hover:border-[#7A1220] rounded-none w-10 h-10"
                    data-testid="outcomes-prev"
                  />
                  <CarouselNext
                    className="static translate-y-0 translate-x-0 bg-transparent border border-white/15 text-white hover:bg-[#7A1220] hover:border-[#7A1220] rounded-none w-10 h-10"
                    data-testid="outcomes-next"
                  />
                </div>
              </div>
            </Carousel>
          </Reveal>
        </div>
      </section>

      {/* MORE MOMENTS — atmosphere collage */}
      <section
        className="relative border-t border-white/5 px-6 sm:px-10 lg:px-16 py-12 sm:py-16 overflow-hidden"
        data-testid="moments-section"
      >
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <Overline dataTestid="moments-overline" num="06">Більше — в Instagram</Overline>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light mt-6 max-w-3xl leading-tight">
              Моменти зі студії,<br />
              <span className="italic text-[#A01828]">у які ти зможеш поринути</span>.
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {[
              { type: "video", src: HERO_VIDEO, poster: IMAGES.trainer, ratio: "aspect-[3/4]" },
              { type: "video", src: MOMENT_VIDEO_1, poster: IMAGES.trainerAlt, ratio: "aspect-[3/4]" },
              { type: "image", src: IMAGES.group, ratio: "aspect-[3/4]" },
              { type: "video", src: ATMOSPHERE_VIDEO, poster: IMAGES.course, ratio: "aspect-[3/4]" },
              { type: "video", src: MOMENT_VIDEO_2, poster: IMAGES.trainer, ratio: "aspect-[3/4]" },
              { type: "image", src: IMAGES.course, ratio: "aspect-[3/4]" },
            ].map((m, i) => (
              <a
                key={i}
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`moment-${i}`}
                className={`relative ${m.ratio} bg-[#0A0A0C] overflow-hidden group`}
              >
                {m.type === "video" ? (
                  <VideoTile
                    src={m.src}
                    poster={m.poster}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                  />
                ) : (
                  <img
                    src={m.src}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Instagram className="w-5 h-5 text-white drop-shadow-lg" />
                </div>
              </a>
            ))}
          </div>

          <Reveal delay={200}>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="moments-instagram-cta"
              className="mt-10 inline-flex items-center gap-3 border border-white/15 px-7 h-12 text-xs uppercase tracking-[0.2em] hover:bg-[#7A1220] hover:border-[#7A1220] transition-all"
            >
              <Instagram className="w-4 h-4" />
              @grand_dance_studio_wro
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>
      {/* PRIVATE LESSONS & MASTERCLASSES */}
      <Section id="private" className="border-t border-white/5">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline dataTestid="private-overline" num="07">Окремі формати</Overline>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-light mt-4 leading-tight">
                Не з групи?<br />
                <span className="italic text-[#A01828]">Це теж можливо</span>.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-5 text-white/60 text-sm sm:text-base leading-relaxed max-w-md">
                Для тих, хто хоче більш індивідуального формату — або заводить танці у свою команду.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 space-y-px bg-white/5">
            <Reveal delay={100}>
              <div className="bg-[#0E0E11] p-7 sm:p-9" data-testid="private-individual">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#A01828] font-medium">
                      Індивідуально
                    </p>
                    <h3 className="font-serif-display text-2xl sm:text-3xl font-light mt-2 leading-tight">
                      Solo Lady · Пари
                    </h3>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-white/50">від</p>
                    <p className="font-serif-display text-4xl text-[#FDFBF7]">250 zł</p>
                  </div>
                </div>
                <p className="mt-4 text-white/65 text-sm sm:text-base leading-relaxed">
                  Заняття «один на один» або у парі — з фокусом на твоїх цілях: техніка,
                  пластика, постановка, підготовка до події.
                </p>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="bg-[#0E0E11] p-7 sm:p-9" data-testid="private-masterclass">
                <p className="text-xs uppercase tracking-[0.3em] text-[#A01828] font-medium">
                  Майстер-класи
                </p>
                <h3 className="font-serif-display text-2xl sm:text-3xl font-light mt-2 leading-tight">
                  Ком'юніті · Корпоративи · Тімбілдинги
                </h3>
                <p className="mt-4 text-white/65 text-sm sm:text-base leading-relaxed">
                  Жіночий захід, day-off для команди, корпоратив — формати під твій
                  запит. Деталі та умови — в Instagram-direct.
                </p>
              </div>
            </Reveal>
            <Reveal delay={300}>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="private-instagram-cta"
                className="bg-[#7A1220] hover:bg-[#A01828] transition-colors p-7 flex items-center justify-between gap-4 group"
              >
                <span>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/70">
                    Запис та узгодження
                  </p>
                  <p className="font-serif-display text-2xl text-white mt-1">
                    Напиши в Instagram-direct
                  </p>
                </span>
                <Instagram className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
              </a>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="border-t border-white/5">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <Reveal>
              <Overline dataTestid="faq-overline" num="08">Питання</Overline>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-light mt-6 leading-tight">
                Все, що ти хотіла
                <br />
                <span className="italic text-[#A01828]">запитати</span>.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {[
                {
                  q: "Чи можна прийти без досвіду?",
                  a: "Так. Початкові групи створені саме для тих, хто ніколи не танцював. Ми починаємо з нуля — спокійно, тепло, без оцінок.",
                },
                {
                  q: "Чи потрібен партнер?",
                  a: "Ні. Lady Latina та Latin Solo — це сольні жіночі стилі. Партнер не потрібен.",
                },
                {
                  q: "Що вдягнути на перше заняття?",
                  a: (
                    <div className="space-y-5">
                      <p>
                        Бери свої кросівки на перезувне, зручні лосіни/шорти, майка/топ — і ти готова.
                      </p>
                      <img
                        src={IMAGES.dressCode}
                        alt="Зручний одяг для пробного заняття — топи, лосіни/шорти, чисті кросівки"
                        loading="lazy"
                        data-testid="dress-code-image"
                        className="w-full h-auto block border border-white/10"
                      />
                    </div>
                  ),
                },
                {
                  q: "Як знайти зал?",
                  a: (
                    <div className="space-y-3">
                      <p className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-[#A01828] mt-1 shrink-0" />
                        <span>
                          <span className="text-[#FDFBF7]">{STUDIO_ADDRESS.full}</span>
                          <br />
                          <a
                            href={STUDIO_ADDRESS.mapUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="map-link"
                            className="text-[#A01828] hover:underline text-sm"
                          >
                            Відкрити на карті →
                          </a>
                        </span>
                      </p>
                      <p>
                        Детальна відео-інструкція «ЯК ЗНАЙТИ» — у нас в Instagram у закріплених сторіс (Highlights).
                      </p>
                      <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="faq-instagram-link"
                        className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#A01828] transition-colors mt-1"
                      >
                        <Instagram className="w-4 h-4" /> @grand_dance_studio_wro
                      </a>
                    </div>
                  ),
                },
                {
                  q: "Чи можна приєднатися до групи, якщо я ніколи не танцювала?",
                  a: "Так. Відкрита початкова група Аліни (п'ятниця 19:00) — для тих, хто ніколи не танцював. Групи Анастасії заповнені — можна залишити контакти в листі очікування на березень.",
                },
                {
                  q: "Як забронювати пробне заняття?",
                  a: "Натисни «Забронювати пробне заняття» — і одразу перейдеш на безпечну сторінку оплати Stripe. Місце фіксується після оплати 50 zł.",
                },
                {
                  q: "Як оплатити пробне заняття?",
                  a: "Оплата проходить безпечно через Stripe — банківською карткою. Підтвердження приходить миттєво.",
                },
              ].map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-white/10 last:border-0"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-serif-display text-xl sm:text-2xl text-white/90 hover:text-[#FDFBF7] hover:no-underline py-6">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/65 text-base leading-relaxed pb-6">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>
      {/* FINAL CTA */}
      <section
        id="final-cta"
        data-testid="final-cta-section"
        className="relative border-t border-white/5 px-6 sm:px-10 lg:px-16 py-16 sm:py-24 overflow-hidden"
      >
        <div className="absolute inset-0 opacity-30">
          <img src={IMAGES.benefit} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C] via-[#0A0A0C]/85 to-[#0A0A0C]" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <Overline num="09">Час починати</Overline>
          </Reveal>
          <Reveal delay={150}>
            <h2
              data-testid="final-cta-headline"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light mt-4 leading-[1.05]"
            >
              Твоє тіло хоче
              <br />
              цієї <span className="italic text-[#A01828]">свободи</span>,
              <br />
              і воно готове.
            </h2>
          </Reveal>
          <Reveal delay={250}>
            <p className="mt-5 text-white/70 text-lg sm:text-xl font-light">
              Просто зроби перший крок.
            </p>
          </Reveal>
          <Reveal delay={350}>
            <motion.button
              onClick={() => openBooking()}
              data-testid="final-cta-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="mt-8 inline-flex items-center gap-3 bg-[#7A1220] hover:bg-[#A01828] transition-colors duration-300 text-white px-10 h-16 text-xs uppercase tracking-[0.25em] font-medium"
            >
              Забронювати пробне заняття
              <ArrowUpRight className="w-4 h-4" />
            </motion.button>
          </Reveal>
          <Reveal delay={500}>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] text-white/40">
              50 zł · Wrocław · Grand Dance Studio
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 px-6 sm:px-10 lg:px-16 py-10" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
          <div>
            <img src={IMAGES.logo} alt="Grand Dance Studio" className="h-14 w-auto opacity-95 drop-shadow-[0_2px_18px_rgba(160,24,40,0.4)]" />
            <a
              href={STUDIO_ADDRESS.mapUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-address"
              className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#A01828] transition-colors"
            >
              <MapPin className="w-3 h-3" /> {STUDIO_ADDRESS.full}
            </a>
          </div>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#A01828] transition-colors"
            data-testid="footer-instagram"
          >
            <Instagram className="w-4 h-4" />
            @grand_dance_studio_wro
          </a>
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-white/5 text-xs text-white/30 flex flex-col sm:flex-row gap-2 justify-between">
          <p>© {new Date().getFullYear()} Grand Dance Studio. All rights reserved.</p>
          <p>Wrocław · Lady Latina · Latin Solo</p>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div
        className="fixed bottom-0 inset-x-0 z-30 sm:hidden bg-gradient-to-t from-black via-black/95 to-black/0 pt-6 pb-4 px-5"
        data-testid="sticky-mobile-cta"
      >
        <button
          onClick={() => openBooking()}
          data-testid="sticky-cta-button"
          className="w-full h-14 bg-[#7A1220] hover:bg-[#A01828] active:scale-[0.99] transition-all duration-300 text-white text-xs uppercase tracking-[0.25em] font-medium flex items-center justify-center gap-2"
        >
          Забронювати · 50 zł
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>

      <BookingDialog open={openTrial} onOpenChange={setOpenTrial} packageId="trial_class" defaultGroup={defaultGroup} />
      <BookingDialog open={openCourse} onOpenChange={setOpenCourse} packageId="hipno_bedra" />
      <WaitlistDialog open={openWaitlist} onOpenChange={setOpenWaitlist} />
    </div>
  );
}
